/* eslint-disable no-redeclare */
import {
  User,
  UserRole,
  UserRight,
  UserRightAccessType,
  UserRightSection,
  isNilOrEmpty,
  todayDateTime,
} from '@rentguru/commons-utils';
import { isUnauthorizedGraphQLError, recordWasUpdated } from '@up2rent/graphql';
import React, { useContext, useEffect, useMemo } from 'react';

import {
  createUserRoleApi,
  deleteUserRoleApi,
  fetchUserRolesApi,
  updateUserRoleApi,
  type UserRoleApiRow,
} from '../auth/teamRoleApi';

import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { useUser } from './UserContext';
import { FETCH_RETRY_DELAY_MS } from './UsersContext';

export type ActionUserRole =
  | {
      type: 'SHOULD_FETCH_USER_ROLE' | 'IS_FETCHING_USER_ROLE';
    }
  | {
      type: 'ADD_USER_ROLE';
      payload: { userRole: UserRole };
    }
  | {
      type: 'FETCHED_USER_ROLE';
      payload: { userRoles: UserRole[] };
    }
  | {
      type: 'UPDATE_USER_ROLE';
      payload: { userRole: UserRole };
    }
  | {
      type: 'DELETE_USER_ROLE';
      payload: { id: string };
    };

export interface UserRolesContext extends UserRoleState {
  getUserRole: (id: string) => UserRole | undefined;
  createUserRole: (input: Omit<UserRole, 'id' | 'clientId' | 'readId'>) => Promise<UserRole>;
  updateUserRole: (original: UserRole, updates: Partial<UserRole>, promptRefreshToken?: boolean) => Promise<UserRole>;
  deleteUserRole: (id: string) => Promise<void>;
  getUsersWithRole: (id: string) => User[];
  setFetchUserRole: () => void;
}

interface UserRoleState {
  userRoles: UserRole[];
  loading: boolean;
  shouldFetch: boolean;
  error: string | null;
}

export const userRoleReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_USER_ROLE':
      if (state.UserRole.loading || state.UserRole.shouldFetch) {
        return state;
      }
      return {
        ...state,
        UserRole: { ...state.UserRole, shouldFetch: true },
      };
    case 'IS_FETCHING_USER_ROLE':
      if (state.UserRole.loading) {
        return state;
      }
      return {
        ...state,
        UserRole: { ...state.UserRole, loading: true },
      };
    case 'FETCHED_USER_ROLE':
      return {
        ...state,
        UserRole: {
          ...state.UserRole,
          data: action.payload.userRoles,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_USER_ROLE':
      // Check if already present - If already added by this user or coming from another user
      if (state.UserRole.data?.find((object) => object.id === action.payload.userRole.id)) {
        return state;
      }

      return {
        ...state,
        UserRole: {
          ...state.UserRole,
          data: [...state.UserRole.data, action.payload.userRole],
        },
      };
    case 'UPDATE_USER_ROLE':
      // No data
      if (isNilOrEmpty(state.UserRole.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.UserRole.data?.find((object) => object.id === action.payload.userRole.id);
      if (!currentObject) {
        return {
          ...state,
          UserRole: {
            ...state.UserRole,
            data: [...state.UserRole.data, action.payload.userRole],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.userRole)) {
        return state;
      }

      // Update
      return {
        ...state,
        UserRole: {
          ...state.UserRole,
          data: state.UserRole.data.map((object) => {
            if (object.id === action.payload.userRole.id) {
              return action.payload.userRole;
            }
            return object;
          }),
        },
      };
    case 'DELETE_USER_ROLE':
      if (isNilOrEmpty(state.UserRole.data)) {
        return state;
      }
      return {
        ...state,
        UserRole: {
          ...state.UserRole,
          data: state.UserRole.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

// Map a DSQL user_role row (Better Auth plugin) to the legacy UserRole shape the
// rest of the app consumes. clientId/readId/_version are AppSync artifacts kept
// only for shape compatibility.
const toUserRole = (row: UserRoleApiRow, clientId: string): UserRole => ({
  id: row.id,
  name: row.name,
  userRights: row.userRights.map((right) => ({
    section: right.section as UserRightSection,
    access: right.access as UserRightAccessType[],
  })),
  clientId,
  readId: '',
  _version: 1,
});

const toApiRights = (userRights?: UserRight[] | null): { section: string; access: string[] }[] =>
  (userRights ?? []).map((right) => ({ section: right.section, access: right.access ?? [] }));

const fetchUserRoles = async (clientId: string): Promise<UserRole[]> => {
  const { roles } = await fetchUserRolesApi();
  return roles.map((role) => toUserRole(role, clientId));
};

export const UserRolesContext = React.createContext<UserRolesContext | null>(null);

export const UserRolesContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    UserRole: { data: userRoles, loading: userRolesLoading, shouldFetch },
    User: { data: users, loading: usersLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { clientId } = useUser();

  const setFetchUserRole = () => {
    contextDispatch({ type: 'SHOULD_FETCH_USER_ROLE' });
  };

  const loading = userRolesLoading || usersLoading;

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_USER_ROLE' });
      try {
        const result = await fetchUserRoles(clientId!);
        contextDispatch({ type: 'FETCHED_USER_ROLE', payload: { userRoles: result } });
      } catch (error) {
        // Always resolve the loading flag, otherwise ContextLoader shows an infinite LoadingScreen.
        console.error(error);
        contextDispatch({ type: 'FETCHED_USER_ROLE', payload: { userRoles: [] } });
        if (!isUnauthorizedGraphQLError(error)) {
          // Transient failure: schedule a refetch instead of caching emptiness as valid data.
          setTimeout(() => contextDispatch({ type: 'SHOULD_FETCH_USER_ROLE' }), FETCH_RETRY_DELAY_MS);
        }
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  const getUserRole = (id: string): UserRole | undefined => {
    if (loading) {
      return undefined;
    }
    return userRoles.find((u) => u.id === id);
  };

  const createUserRole = async (input: Omit<UserRole, 'id' | 'clientId' | 'readId'>) => {
    const { role } = await createUserRoleApi({ name: input.name, userRights: toApiRights(input.userRights) });
    const created = toUserRole(role, clientId!);
    contextDispatch({ type: 'ADD_USER_ROLE', payload: { userRole: created } });
    return created;
  };

  const getUsersWithRole = (id: string) => {
    return users.filter((user) => user.userRoleId === id);
  };

  const updateUserRole = async (original: UserRole, updates: Partial<UserRole>): Promise<UserRole> => {
    await updateUserRoleApi({
      id: original.id,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.userRights !== undefined ? { userRights: toApiRights(updates.userRights) } : {}),
    });
    // The plugin re-snapshots the rights onto every member holding this role, so
    // the next request's authorizer already reflects the change — no token
    // refresh needed (rights are read from DSQL, not carried in the JWT).
    const updated: UserRole = { ...original, ...updates, _version: (original._version ?? 0) + 1 };
    contextDispatch({ type: 'UPDATE_USER_ROLE', payload: { userRole: updated } });
    return updated;
  };

  const deleteUserRole = async (id: string) => {
    await deleteUserRoleApi(id);
    contextDispatch({ type: 'DELETE_USER_ROLE', payload: { id } });
  };

  const values = useMemo(
    () => ({
      shouldFetch,
      userRoles,
      error: null,
      loading,
      getUserRole,
      createUserRole,
      updateUserRole,
      deleteUserRole,
      getUsersWithRole,
      setFetchUserRole,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userRoles, loading, shouldFetch]
  );

  return <UserRolesContext.Provider value={values}>{children}</UserRolesContext.Provider>;
};

export const useUserRoles = (): UserRolesContext => {
  const context = useContext<UserRolesContext | null>(UserRolesContext);

  if (context === undefined) {
    throw new Error('`useUserRoles` hook must be used within a `UserRolesContextProvider` component');
  }
  return context as UserRolesContext;
};
